import React, { useState, useRef, useEffect } from 'react';
import { FiCheckCircle, FiAlertCircle, FiCamera, FiEdit3, FiArrowRight, FiDollarSign, FiUser } from 'react-icons/fi';
import { verifyQr, labourerScan } from '../services/api';

type Step = 'scan' | 'verified' | 'details' | 'confirm' | 'success' | 'error';

const normalizeQrValue = (rawValue: string) => {
  const value = rawValue.trim();
  if (!value) return '';

  try {
    const url = new URL(value);
    return url.searchParams.get('qr') || url.searchParams.get('code') || value;
  } catch {
    return value;
  }
};

const LabourerPage: React.FC = () => {
  const [step, setStep] = useState<Step>('scan');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [qrInfo, setQrInfo] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [form, setForm] = useState({ fullName: '', mobile: '', city: '', upiId: '' });
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Check for QR code in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qr = normalizeQrValue(params.get('qr') || params.get('code') || '');
    if (qr) {
      setQrCode(qr);
      handleVerifyQr(qr);
    }
    return () => stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setScanning(false);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setScanning(true);
      scanFrame();
    } catch {
      setErrorMsg('Camera access denied. Please enter QR code manually.');
    }
  };

  const scanFrame = () => {
    if (!videoRef.current || !canvasRef.current || !streamRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx || video.videoWidth === 0) {
      requestAnimationFrame(scanFrame);
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);
    // Use BarcodeDetector if available (Chrome/Edge)
    if ('BarcodeDetector' in window) {
      const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
      detector.detect(canvas).then((barcodes: any[]) => {
        if (barcodes.length > 0) {
          const code = normalizeQrValue(barcodes[0].rawValue || '');
          stopCamera();
          setQrCode(code);
          handleVerifyQr(code);
          return;
        }
        if (streamRef.current) requestAnimationFrame(scanFrame);
      }).catch(() => {
        if (streamRef.current) requestAnimationFrame(scanFrame);
      });
    } else {
      // Fallback: no BarcodeDetector — just keep camera running, user enters manually
      if (streamRef.current) requestAnimationFrame(scanFrame);
    }
  };

  const handleVerifyQr = async (code: string) => {
    const normalizedCode = normalizeQrValue(code);
    if (!normalizedCode) return;

    setLoading(true);
    setErrorMsg('');
    try {
      setQrCode(normalizedCode);
      const res = await verifyQr(normalizedCode);
      if (res.data.success) {
        setQrInfo(res.data.data);
        setStep('verified');
      } else {
        setErrorMsg(res.data.message);
        setStep('error');
      }
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || 'Invalid or already used QR code');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitClaim = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await labourerScan({ ...form, qrCode: qrCode.trim() });
      if (res.data.success) {
        setResult(res.data.data);
        setStep('success');
      } else {
        setErrorMsg(res.data.message);
        setStep('error');
      }
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || 'Something went wrong');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('scan');
    setQrCode('');
    setQrInfo(null);
    setResult(null);
    setErrorMsg('');
    setForm({ fullName: '', mobile: '', city: '', upiId: '' });
  };

  const stepNum = step === 'scan' ? 1 : step === 'verified' ? 2 : step === 'details' ? 2 : step === 'confirm' ? 3 : 4;

  const StepIndicator = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 28 }}>
      {['Scan QR', 'Your Details', 'Confirm', 'Done'].map((label, i) => {
        const num = i + 1;
        const active = num <= stepNum;
        const current = num === stepNum;
        return (
          <React.Fragment key={i}>
            {i > 0 && <div style={{ width: 32, height: 2, background: active ? '#43A047' : '#e5e7eb', margin: '0 2px' }} />}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
                background: active ? (current ? '#43A047' : '#dcfce7') : '#f3f4f6',
                color: active ? (current ? '#fff' : '#16a34a') : '#9ca3af',
                border: current ? '2px solid #43A047' : '2px solid transparent',
              }}>{active && num < stepNum ? '✓' : num}</div>
              <span style={{ fontSize: 10, fontWeight: 500, color: active ? '#374151' : '#9ca3af' }}>{label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className="labourer-page">
      <header className="labourer-header">
        <img src="/logo.png" alt="Lancer" style={{ height: 44 }} />
        <h2>LANCER Loyalty Rewards</h2>
      </header>

      <div className="labourer-content">
        <StepIndicator />

        {/* Step 1: Scan or Enter QR Code */}
        {step === 'scan' && (
          <div className="card" style={{ padding: 28 }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e8f5e9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <FiCamera style={{ fontSize: 24, color: '#43A047' }} />
              </div>
              <h3 style={{ marginBottom: 4 }}>Scan Product QR Code</h3>
              <p style={{ fontSize: 13, color: '#6b7280' }}>Scan with your camera or enter the code manually</p>
            </div>

            {scanning ? (
              <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', marginBottom: 16, background: '#000' }}>
                <video ref={videoRef} style={{ width: '100%', display: 'block', borderRadius: 12 }} playsInline muted />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 200, height: 200, border: '3px solid rgba(67,160,71,0.7)', borderRadius: 16 }} />
                </div>
                <button onClick={stopCamera} className="btn"
                  style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 12, padding: '8px 16px' }}>
                  Cancel
                </button>
              </div>
            ) : (
              <button onClick={startCamera} className="btn"
                style={{ width: '100%', padding: 14, background: '#f0fdf4', color: '#16a34a', border: '2px dashed #86efac', borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
                <FiCamera /> Open Camera to Scan
              </button>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '12px 0' }}>
              <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
              <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>OR enter manually</span>
              <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <input className="form-control" placeholder="Enter QR code" value={qrCode}
                onChange={(e) => setQrCode(e.target.value)}
                style={{ flex: 1, background: '#fff', border: '1px solid #d1d5db', color: '#1f2937' }} />
              <button className="btn btn-primary" disabled={loading || !qrCode.trim()}
                onClick={() => handleVerifyQr(qrCode)}
                style={{ padding: '10px 20px', fontSize: 14 }}>
                {loading ? '...' : <><FiArrowRight /></>}
              </button>
            </div>
          </div>
        )}

        {/* Step 2a: QR Verified — show reward info */}
        {step === 'verified' && qrInfo && (
          <div className="card" style={{ padding: 28, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#e8f5e9', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <FiCheckCircle style={{ fontSize: 24, color: '#43A047' }} />
            </div>
            <h3 style={{ color: '#16a34a', marginBottom: 4 }}>QR Code Valid!</h3>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>Product: <strong>{qrInfo.productName}</strong></p>
            <div style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderRadius: 14, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, marginBottom: 4 }}>Your Cash Reward</div>
              <div style={{ fontSize: 36, fontWeight: 800, color: '#16a34a', letterSpacing: -1 }}>₹{qrInfo.cashReward}</div>
            </div>
            <p style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 20 }}>Enter your details to claim this reward to your UPI</p>
            <button className="btn btn-primary" onClick={() => setStep('details')}
              style={{ width: '100%', padding: 14, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              Continue <FiArrowRight />
            </button>
          </div>
        )}

        {/* Step 2b: Enter details */}
        {step === 'details' && (
          <div className="card" style={{ padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiUser style={{ fontSize: 18, color: '#3b82f6' }} />
              </div>
              <div>
                <h3 style={{ margin: 0 }}>Your Details</h3>
                <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>We need your info to process the payout</p>
              </div>
            </div>

            <div className="form-group">
              <label>Full Name *</label>
              <input className="form-control" placeholder="Enter your name" value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })} required
                style={{ background: '#fff', border: '1px solid #d1d5db', color: '#1f2937' }} />
            </div>
            <div className="form-group">
              <label>Mobile Number *</label>
              <input className="form-control" type="tel" placeholder="10-digit mobile" value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })} required maxLength={10}
                style={{ background: '#fff', border: '1px solid #d1d5db', color: '#1f2937' }} />
            </div>
            <div className="form-group">
              <label>City *</label>
              <input className="form-control" placeholder="Your city" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} required
                style={{ background: '#fff', border: '1px solid #d1d5db', color: '#1f2937' }} />
            </div>
            <div className="form-group">
              <label>UPI ID *</label>
              <input className="form-control" placeholder="e.g. name@paytm, 9876543210@upi" value={form.upiId}
                onChange={(e) => setForm({ ...form, upiId: e.target.value })} required
                style={{ background: '#fff', border: '1px solid #d1d5db', color: '#1f2937' }} />
            </div>
            <button className="btn btn-primary" disabled={!form.fullName || !form.mobile || !form.city || !form.upiId}
              onClick={() => setStep('confirm')}
              style={{ width: '100%', padding: 14, fontSize: 15, marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              Review & Confirm <FiArrowRight />
            </button>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 'confirm' && qrInfo && (
          <div className="card" style={{ padding: 28 }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#fef3c7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                <FiDollarSign style={{ fontSize: 24, color: '#d97706' }} />
              </div>
              <h3 style={{ marginBottom: 4 }}>Confirm Payout</h3>
              <p style={{ fontSize: 13, color: '#6b7280' }}>Review your details before claiming</p>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: 12, padding: 20, marginBottom: 20 }}>
              {[
                { label: 'Product', value: qrInfo.productName },
                { label: 'Reward Amount', value: `₹${qrInfo.cashReward}`, bold: true, color: '#16a34a' },
                { label: 'Name', value: form.fullName },
                { label: 'Mobile', value: form.mobile },
                { label: 'City', value: form.city },
                { label: 'UPI ID', value: form.upiId, bold: true },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < 5 ? '1px solid #e5e7eb' : 'none' }}>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>{item.label}</span>
                  <span style={{ fontSize: 13, fontWeight: item.bold ? 700 : 500, color: item.color || '#1f2937' }}>{item.value}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn" onClick={() => setStep('details')}
                style={{ flex: 1, padding: 14, background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <FiEdit3 /> Edit
              </button>
              <button className="btn btn-primary" onClick={handleSubmitClaim} disabled={loading}
                style={{ flex: 2, padding: 14, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {loading ? 'Processing...' : <><FiCheckCircle /> Confirm & Claim</>}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 'success' && result && (
          <div className="card success-card" style={{ padding: '48px 28px' }}>
            <div className="success-icon">
              <FiCheckCircle />
            </div>
            <h3 style={{ color: '#16a34a' }}>Reward Claimed!</h3>
            <div className="success-amount">₹{result.amount}</div>
            <p style={{ color: '#6b7280', marginBottom: 8, fontSize: 14 }}>Payout will be processed to</p>
            <div style={{ background: '#f0fdf4', padding: '10px 20px', borderRadius: 8, display: 'inline-block', marginBottom: 24 }}>
              <strong style={{ color: '#16a34a' }}>{result.upiId}</strong>
            </div>
            <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 24 }}>You will receive the amount in your UPI within 24 hours</p>
            <button className="btn btn-primary" onClick={reset}
              style={{ width: '100%', justifyContent: 'center', padding: 14, fontSize: 15 }}>
              Scan Another QR
            </button>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div className="card success-card" style={{ padding: '48px 28px' }}>
            <div className="success-icon" style={{ background: '#FFEBEE', color: '#e53935' }}>
              <FiAlertCircle />
            </div>
            <h3 style={{ color: '#e53935' }}>Failed</h3>
            <p style={{ color: '#6b7280', margin: '10px 0 24px', fontSize: 14 }}>{errorMsg}</p>
            <button className="btn btn-primary" onClick={reset}
              style={{ width: '100%', justifyContent: 'center', padding: 14 }}>
              Try Again
            </button>
          </div>
        )}

        <p style={{ textAlign: 'center', fontSize: 12, color: '#999', marginTop: 24 }}>
          Sail Abrasive Pvt Ltd &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default LabourerPage;
